/* ============================================================
 * カート上のラッピング。
 *
 * 大きく2つの役割があります。
 *   ① ラッピングを選んで本体に付ける（このファイルの後半）
 *   ② 付けたラッピングを本体に連動させる（このファイルの前半）
 *
 * --- ② 連動のしくみ ---
 *
 * 本体とラッピングは、カート追加時に同じ組ID（_pb_group）で結ばれています。
 * ラッピング側には _pb_child: 'true' が付きます。
 *
 * ・本体を削除 → 同じ組のラッピングも一緒に削除
 * ・ラッピングを削除 → 本体はそのまま残す
 *
 * 二段構えにしています。
 *   ① 削除ボタンを横取りし、本体とラッピングを1回のリクエストでまとめて消す
 *      （途中でラッピングだけが残って見える瞬間をなくすため）
 *   ② それ以外の経路で本体が消された場合に備え、親のいないラッピングを掃除する
 * ============================================================ */

(function () {
  // このテーマはカート関連のファイルが複数あり、二重に読み込まれることがあります。
  // リスナーが2つ付くと1回のクリックが2回効いてしまうため、一度だけ動くようにします。
  if (window.__pbCartReady) return;
  window.__pbCartReady = true;

  const GROUP_KEY = '_pb_group';
  const CHILD_KEY = '_pb_child';

  let cartItems = [];
  let busy = false;

  const rootUrl = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const propsOf = (item) => (item && item.properties) || {};
  const groupOf = (item) => propsOf(item)[GROUP_KEY] || null;
  const isChild = (item) => propsOf(item)[CHILD_KEY] === 'true';

  /* ---------- 判定 ---------- */

  /* その組の本体（親）が残っている組IDの一覧です。 */
  function groupsWithParent(items) {
    const groups = new Set();
    items.forEach((item) => {
      const group = groupOf(item);
      if (group && !isChild(item)) groups.add(group);
    });
    return groups;
  }

  /* 親がいなくなったラッピングを探します。 */
  function orphanedChildren(items) {
    const alive = groupsWithParent(items);
    const updates = {};
    items.forEach((item) => {
      const group = groupOf(item);
      if (group && isChild(item) && !alive.has(group)) updates[item.key] = 0;
    });
    return updates;
  }

  /* 指定の本体にぶら下がっているラッピングを探します。 */
  function childrenOf(items, parent) {
    const group = groupOf(parent);
    if (!group || isChild(parent)) return [];
    return items.filter((item) => groupOf(item) === group && isChild(item));
  }

  /* ---------- カートの更新 ---------- */

  async function applyUpdates(updates) {
    if (busy || Object.keys(updates).length === 0) return false;
    busy = true;
    try {
      const response = await fetch(`${rootUrl()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error('cart/update.js が失敗しました');
      window.location.reload();
      return true;
    } catch (error) {
      console.error('[product-builder] ラッピングの連動削除に失敗しました', error);
      busy = false;
      return false;
    }
  }

  async function refreshCart() {
    try {
      const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      cartItems = cart.items || [];
      reorderRows();
      refreshAddButtons();
      lockWrappingRows();
      sweepOrphans(cartItems);
    } catch (error) {
      /* カートが取れないときは何もしません。 */
    }
  }

  function sweepOrphans(items) {
    if (!Array.isArray(items)) return;
    applyUpdates(orphanedChildren(items));
  }

  /* ---------- 並び順の調整 ---------- */

  /*
   * カートの並び順は Shopify 側で決まるため、ラッピングが本体より前に出ることがあります。
   * 対応関係が読み取りにくくなるので、ラッピングの行を本体の直下へ移動します。
   *
   * この移動が安全なのは、数量欄の name が `updates[<行のキー>]` になっているからです。
   * 配列形式の `updates[]` はフォーム内の出現順で cart.items に対応づくため、行を
   * 並び替えると購入手続きの送信時に別の行へ数量が適用されます。
   * main-cart-items.liquid の数量欄を配列形式へ戻す場合は、この移動も必ず取り除いてください。
   */
  function reorderRows() {
    cartItems.forEach((item, index) => {
      if (!isChild(item)) return;

      const group = groupOf(item);
      const parentIndex = cartItems.findIndex((x) => groupOf(x) === group && !isChild(x));
      if (parentIndex === -1) return;

      const childRow = document.getElementById(`CartItem-${index + 1}`);
      const parentRow = document.getElementById(`CartItem-${parentIndex + 1}`);
      if (!childRow || !parentRow || childRow === parentRow) return;
      if (parentRow.nextElementSibling === childRow) return;

      parentRow.insertAdjacentElement('afterend', childRow);
    });
  }

  /* ---------- ① 削除ボタンの横取り ---------- */

  /*
   * 本体を削除するときだけ介入し、ラッピングもまとめて消します。
   * 判定は同期的に行う必要があるため、手元に持っているカート内容を使います
   * （このカートは読み込み時とカート更新のたびに最新化しています）。
   */
  function onRemoveClick(event) {
    const button = event.target.closest('cart-remove-button');
    if (!button || busy) return;

    const line = Number(button.dataset.index);
    if (!line) return;

    const target = cartItems[line - 1];
    if (!target) return;

    const children = childrenOf(cartItems, target);
    if (children.length === 0) return; // ラッピングが無い商品は通常どおり削除させます

    // テーマ本来の削除処理を止め、まとめて消す方に差し替えます。
    event.preventDefault();
    event.stopImmediatePropagation();

    const updates = { [target.key]: 0 };
    children.forEach((child) => {
      updates[child.key] = 0;
    });
    applyUpdates(updates);
  }

  /* ============================================================
   * ① ラッピングを選んで本体に付ける
   * ============================================================ */

  const modal = document.querySelector('[data-pbw]');
  const configEl = document.querySelector('[data-pbw-config]');
  const config = (() => {
    if (!configEl) return null;
    try {
      return JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('[product-builder] ラッピングの設定を読めませんでした', error);
      return null;
    }
  })();

  // いまモーダルで扱っている本体の情報です。
  let target = null;

  const pick = (selector) => (modal ? modal.querySelector(selector) : null);

  /* すでにこの本体に付いているラッピングを全て探します。 */
  function wrappingsOf(itemKey) {
    const parent = cartItems.find((item) => item.key === itemKey);
    return parent ? childrenOf(cartItems, parent) : [];
  }

  /*
   * 本体の個数が、ラッピング合計の上限です。商品1個につき1つまでのため。
   *
   * HTML の data-quantity はページ描画時の値なので、数量を変えた直後は古くなります。
   * 常に手元のカート内容（読み込み時とカート更新のたびに最新化）を優先します。
   */
  function maxWrapping() {
    if (!target) return 1;
    const parent = cartItems.find((item) => item.key === target.key);
    return Math.max(1, parent ? parent.quantity : target.quantity);
  }

  const itemCards = () =>
    modal ? Array.from(modal.querySelectorAll('[data-pbw-item]')) : [];

  const quantityOf = (card) => target.picks[card.dataset.value] || 0;

  /*
   * ラッピング自体の在庫です。
   * 在庫を追跡していない、または在庫数が取れない場合は縛りません
   * （Liquid は在庫が取得できないと 0 を返すため、在庫切れと区別できません）。
   */
  function stockOf(card) {
    const tracked =
      card.dataset.inventoryManagement === 'shopify' && card.dataset.inventoryPolicy !== 'continue';
    if (!tracked) return Infinity;
    const stock = Number(card.dataset.inventoryQuantity);
    if (!Number.isFinite(stock) || stock <= 0) return Infinity;
    return stock;
  }

  /* 同じラッピングを、他の商品がすでに押さえている数です。 */
  function reservedElsewhere(card) {
    if (!target) return 0;
    const id = Number(card.dataset.value);
    return cartItems
      .filter(
        (item) =>
          item.variant_id === id && isChild(item) && !target.existingKeys.includes(item.key)
      )
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /* このカードで選べる最大数です。在庫から他の予約分を引きます。 */
  function stockRoom(card) {
    const stock = stockOf(card);
    if (stock === Infinity) return Infinity;
    return Math.max(0, stock - reservedElsewhere(card));
  }

  /* いま選ばれている合計です。 */
  function totalPicked() {
    return Object.values(target.picks).reduce((sum, n) => sum + n, 0);
  }

  function setQuantity(card, value) {
    const id = card.dataset.value;
    const current = quantityOf(card);
    // 「商品の個数の残り」と「ラッピングの在庫」の、厳しい方に合わせます。
    const room = Math.min(maxWrapping() - (totalPicked() - current), stockRoom(card));
    const next = Math.min(Math.max(value, 0), Math.max(0, room));
    if (next <= 0) delete target.picks[id];
    else target.picks[id] = next;
    refreshModal();
  }

  function setError(message) {
    const el = pick('[data-pbw-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function refreshModal() {
    if (!modal || !target) return;

    const max = maxWrapping();
    const total = totalPicked();
    // ラッピングを付けない分です。最初は商品の個数がそのまま入ります。
    const none = Math.max(0, max - total);

    pick('[data-pbw-target]').textContent = target.name;
    const qtyEl = pick('[data-pbw-target-qty]');
    if (qtyEl) qtyEl.textContent = `${max}個`;

    const mediaBtn = pick('[data-pbw-zoom-open]');
    const mediaImg = pick('[data-pbw-target-image]');
    if (mediaBtn && mediaImg) {
      mediaBtn.hidden = !target.image;
      if (target.image) {
        mediaImg.src = target.image;
        mediaImg.alt = target.name;
      }
    }

    const noneEl = pick('[data-pbw-none-text]');
    if (noneEl) {
      // 設定を変更した直後は、テーマエディタに古い文言が残っていることがあります。
      // 差し込み文字が地のまま出ないよう、過去の書き方も数字に置き換えます。
      noneEl.textContent = (config.note || 'ラッピングなし [数量]個')
        .replace('[数量]', String(none))
        .replace('[合計]', String(total))
        .replace('[上限]', String(max));
    }

    itemCards().forEach((card) => {
      const qty = quantityOf(card);
      const room = stockRoom(card);
      card.setAttribute('aria-pressed', String(qty > 0));

      const input = card.querySelector('[data-pbw-qty]');
      if (input) {
        input.value = String(qty);
        input.max = String(Math.min(max, room === Infinity ? max : room));
      }
      const minus = card.querySelector('[data-pbw-step="-1"]');
      const plus = card.querySelector('[data-pbw-step="1"]');
      if (minus) minus.disabled = qty <= 0;
      // 「ラッピングなし」の残りが無くなるか、在庫の上限に達したら増やせません。
      if (plus) plus.disabled = none <= 0 || qty >= room;

      // 在庫が少ないときだけ、残りを知らせます。
      const stockEl = card.querySelector('[data-pbw-stock]');
      if (stockEl) {
        const showStock = room !== Infinity && room <= max;
        stockEl.hidden = !showStock;
        if (showStock) {
          stockEl.textContent = (config.stockText || '残り[数量]個です').replace(
            '[数量]',
            String(room)
          );
        }
      }
    });

    // すべて「ラッピングなし」にすると、付いていたラッピングを外せます。
    pick('[data-pbw-done]').disabled = total === 0 && target.existingKeys.length === 0;
  }

  function openModal(button) {
    if (!modal || !config) return;

    const parent = cartItems.find((item) => item.key === button.dataset.key);
    const existing = wrappingsOf(button.dataset.key);

    // 変更のときは、いま付いている内容を初期値にします。
    const picks = {};
    existing.forEach((item) => {
      picks[String(item.variant_id)] = item.quantity;
    });
    // 在庫が減っていることがあるので、この後 refreshModal で範囲に収め直します。

    target = {
      key: button.dataset.key,
      name: button.dataset.name,
      // カートが取れていないときだけ、描画時の値を使います。
      quantity: parent ? parent.quantity : Number(button.dataset.quantity) || 1,
      image: button.dataset.image || '',
      imageLarge: button.dataset.imageLarge || button.dataset.image || '',
      picks: picks,
      existingKeys: existing.map((item) => item.key),
    };

    setError('');
    const doneBtn = pick('[data-pbw-done]');
    if (doneBtn) doneBtn.textContent = config.doneText || '完了';
    modal.hidden = false;
    document.body.classList.add('pbw-lock');
    refreshModal();
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('pbw-lock');
    target = null;
  }

  /*
   * 本体と同じ組IDで結びます。
   * 本体側には組IDが無いため、/cart/change.js でプロパティを足してから
   * ラッピングを追加します（数量・価格は変えません）。
   */
  async function attachWrapping() {
    if (!target || busy) return;
    // 何も選ばず、外すものも無いときは何もしません。
    if (totalPicked() === 0 && target.existingKeys.length === 0) return;

    const doneBtn = pick('[data-pbw-done]');
    if (doneBtn) {
      doneBtn.disabled = true;
      // 押したあと何も変わらないと不安になるので、進行中だと分かる文言に変えます。
      doneBtn.textContent =
        target.existingKeys.length > 0
          ? config.changingText || '変更中…'
          : config.addingText || '追加中…';
    }
    setError('');
    busy = true;

    try {
      const parent = cartItems.find((item) => item.key === target.key);
      if (!parent) throw new Error('本体が見つかりません');

      const group = groupOf(parent) || `pb-${Math.random().toString(36).slice(2, 10)}`;

      // 本体に組IDが無ければ足します。既存のプロパティは残します。
      // ラッピングを付けないときは、紐付ける必要がないので触りません。
      if (!groupOf(parent) && totalPicked() > 0) {
        const merged = { ...propsOf(parent), [GROUP_KEY]: group };
        const res = await fetch(`${rootUrl()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: parent.key, quantity: parent.quantity, properties: merged }),
        });
        if (!res.ok) throw new Error('本体への紐付けに失敗しました');
      }

      // 付け替えのときは、先に古いラッピングをまとめて消します。
      if (target.existingKeys.length > 0) {
        const updates = {};
        target.existingKeys.forEach((key) => {
          updates[key] = 0;
        });
        const res = await fetch(`${rootUrl()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!res.ok) throw new Error('古いラッピングを外せませんでした');
      }

      const items = itemCards()
        .filter((card) => quantityOf(card) > 0)
        .map((card) => ({
          id: Number(card.dataset.value),
          quantity: quantityOf(card),
          properties: {
            [config.trackingKey]: config.trackingValue,
            [GROUP_KEY]: group,
            [CHILD_KEY]: 'true',
            // 「_」なしのプロパティは、お客様にも出荷担当にも見えます。
            [config.targetLabel]: target.name,
          },
        }));

      if (items.length > 0) {
        const res = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error('ラッピングを追加できませんでした');
      }

      window.location.reload();
    } catch (error) {
      console.error('[product-builder] ラッピングの追加に失敗しました', error);
      busy = false;
      if (doneBtn) {
        doneBtn.disabled = false;
        doneBtn.textContent = config.doneText || '完了';
      }
      setError(config.errorText);
    }
  }

  /*
   * ラッピング行の数量欄は、見せるだけで触らせません。
   * CSS だけだとキーボード操作で届いてしまうため、要素側でも無効にします。
   */
  function lockWrappingRows() {
    document.querySelectorAll('.pb-cart-wrapping').forEach((row) => {
      row.querySelectorAll('input, button, select').forEach((el) => {
        if (el.closest('cart-remove-button')) return;
        el.disabled = true;
        el.tabIndex = -1;
      });
    });
  }

  /* すでに付いている本体のボタンを「変更」に差し替えます。 */
  function refreshAddButtons() {
    if (!config) return;
    document.querySelectorAll('[data-pbw-add]').forEach((button) => {
      const label = button.querySelector('[data-pbw-add-text]');
      if (!label) return;
      const has = wrappingsOf(button.dataset.key).length > 0;
      label.textContent = has ? config.changeLabel : config.addLabel;
      button.classList.toggle('pbw-add--has', has);
    });
  }

  /*
   * ラッピングの行から、その本体の「追加ボタン」を探します。
   * 行番号（CartItem-N）で手元のカート内容を引き、同じ組の親を辿ります。
   */
  function parentButtonOf(row) {
    const index = Number((row.id || '').replace('CartItem-', ''));
    const child = cartItems[index - 1];
    if (!child) return null;
    const group = groupOf(child);
    if (!group) return null;
    const parent = cartItems.find((item) => groupOf(item) === group && !isChild(item));
    if (!parent) return null;
    // カートのキーには記号が混ざるため、セレクタに埋め込まず突き合わせます。
    return (
      Array.from(document.querySelectorAll('[data-pbw-add]')).find(
        (button) => button.dataset.key === parent.key
      ) || null
    );
  }

  function bindWrapping() {
    if (!modal || !config) return;

    document.addEventListener('click', (event) => {
      const add = event.target.closest('[data-pbw-add]');
      if (add) {
        openModal(add);
        return;
      }

      // ラッピングの行は、押すと本体の変更モーダルが開きます。
      // 行の中で数量や削除を触らせず、変更はモーダルに集約するためです。
      const wrapRow = event.target.closest('.pb-cart-wrapping');
      if (wrapRow && (!modal || modal.hidden)) {
        // 本体がまだ引けなくても、商品ページへ飛ばさないようにします。
        event.preventDefault();
        const button = parentButtonOf(wrapRow);
        if (button) openModal(button);
        return;
      }
      if (!modal || modal.hidden) return;

      if (event.target.closest('[data-pbw-close]') || event.target === modal) {
        closeModal();
        return;
      }
      const step = event.target.closest('[data-pbw-step]');
      if (step) {
        const card = step.closest('[data-pbw-item]');
        if (card) setQuantity(card, quantityOf(card) + Number(step.dataset.pbwStep));
        return;
      }
      if (event.target.closest('[data-pbw-done]')) attachWrapping();
    });

    // 写真の拡大表示です。小さくて分かりにくいときのための逃げ道です。
    const zoom = document.querySelector('[data-pbw-zoom]');
    if (zoom) {
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-pbw-zoom-open]');
        // ラッピングのカードは自分の写真を、対象商品のボタンは選んだ商品の写真を出します。
        const src = opener ? opener.dataset.zoom || (target && target.imageLarge) : null;
        if (src) {
          const img = zoom.querySelector('[data-pbw-zoom-image]');
          img.src = src;
          img.alt = opener.dataset.zoomAlt || (target ? target.name : '');
          zoom.hidden = false;
          return;
        }
        if (zoom.hidden) return;
        if (event.target.closest('[data-pbw-zoom-close]') || event.target === zoom) {
          zoom.hidden = true;
        }
      });
    }

    modal.addEventListener('change', (event) => {
      const input = event.target.closest('[data-pbw-qty]');
      if (!input || !target) return;
      const card = input.closest('[data-pbw-item]');
      if (!card) return;
      const value = parseInt(input.value, 10);
      setQuantity(card, Number.isFinite(value) ? value : 0);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const zoomEl = document.querySelector('[data-pbw-zoom]');
      if (zoomEl && !zoomEl.hidden) {
        zoomEl.hidden = true;
        return;
      }
      if (modal && !modal.hidden) closeModal();
    });
  }

  /* ---------- 起動 ---------- */

  document.addEventListener('DOMContentLoaded', () => {
    bindWrapping();
    lockWrappingRows();

    // 削除ボタン自身の処理より先に判断するため、キャプチャ段階で受け取ります。
    document.addEventListener('click', onRemoveClick, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const items = event && event.cartData && event.cartData.items;
        if (!Array.isArray(items)) return;
        cartItems = items;
        // カートは再描画されているため、並び順とボタンの文言を整え直します。
        reorderRows();
        refreshAddButtons();
        lockWrappingRows();
        // ② 別の経路で本体が消された場合の保険です。
        sweepOrphans(items);
      });
    }

    refreshCart();
  });
})();
